import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FieldDatepicker,
  FieldInput,
  FieldTextArea,
  FormCol12,
  FormCol3,
  FormCol4,
  FormCol6,
  FormRow,
  toastFailure,
} from 'motion-components';
import { TabContextTitle } from '../components/tab-context-title';
import { OrderPackingTable } from './order-packing-table';
import { useFieldDisabledByStatus } from '../services';
import { Button } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { apiBase } from 'shared-pages/common/api-helpfunc';
import { useApolloClient } from '@apollo/client';
import { gqlEncodePdfAccessToken } from '../../queries';
import { ButtonCalcPackagingMaterials } from './components/button-calc-packaging-materials';

export const OrderPackingTab = ({ data, refetch }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const fieldDisabled = useFieldDisabledByStatus();
  const { values, setFieldValue } = useFormikContext<any>();

  const [isPrintingPdf, setIsPrintingPdf] = useState(false);

  const orderId = Number(data?.id ?? values?.id);
  const canPrintOrderPackingPdf = Number.isInteger(orderId) && orderId > 0;

  async function handlePrintOrderPackingPdf(): Promise<void> {
    if (!canPrintOrderPackingPdf || isPrintingPdf) return;
    try {
      setIsPrintingPdf(true);
      const payload = JSON.stringify({
        type: 'collection-order-packing',
        args: { id: orderId },
        expirationDate: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      });
      const ret = await client.query({
        query: gqlEncodePdfAccessToken,
        variables: { key: payload },
        fetchPolicy: 'no-cache',
      });
      const token = ret?.data?.encodeKey;
      if (!token) {
        throw new Error('Missing PDF access token');
      }

      const url = `${apiBase()}/pdf/access?token=${encodeURIComponent(token)}`;
      const win = window.open(url, '_blank');
      win?.focus();
    } catch (error) {
      console.error('Failed to open request PDF:', error);
      toastFailure(t('Αποτυχία δημιουργίας PDF αιτήματος.'));
    } finally {
      setIsPrintingPdf(false);
    }
  }

  return (
    <div>
      <TabContextTitle label={t('Στοιχεία Συσκευασίας')} badge="3" />
      <div className="mb-3 d-flex">
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={handlePrintOrderPackingPdf}
          disabled={!canPrintOrderPackingPdf || isPrintingPdf}
          title={
            !canPrintOrderPackingPdf
              ? t('Αποθήκευσε πρώτα την συσκευασία για εκτύπωση.')
              : undefined
          }
        >
          {t('Εκτύπωση Συσκευασιών (PDF)')}
        </Button>
      </div>

      <FormRow>
        <FieldDatepicker
          as={FormCol6}
          label={t('Ημερομηνία Αποστολής Συσκευασίας')}
          name="xrbData.packagingDispatchDate"
          disabled={fieldDisabled['xrbData.packagingDispatchDate']}
        />
      </FormRow>
      <FormRow>
        <FieldTextArea
          as={FormCol6}
          label={t('Οδηγίες Συσκευασίας')}
          name="xrbData.packagingInstructions"
          rows={3}
          disabled={fieldDisabled['xrbData.packagingInstructions']}
        />
      </FormRow>

      {(values.xrbData?.aiPackagingMaterialsCalculationsObject != null ||
        values.xrbData?.aiPackagingMaterialsCalculations) && (
        <FormRow className="mb-3">
          <FormCol12>
            <div className="small text-muted mb-1">{t('Υπολογισμός υλικών συσκευασίας (AI)')}</div>
            <pre
              className="border rounded p-2 bg-light mb-0 small"
              style={{ whiteSpace: 'pre-wrap', maxHeight: '320px', overflow: 'auto' }}
            >
              {values.xrbData?.aiPackagingMaterialsCalculationsObject != null
                ? JSON.stringify(values.xrbData.aiPackagingMaterialsCalculationsObject, null, 2)
                : values.xrbData.aiPackagingMaterialsCalculations}
            </pre>
          </FormCol12>
        </FormRow>
      )}

      <h6>Συσκευασίες</h6>
      {data.xrbData?.appropriatePackaging === false && (
        <FieldInput
          as={FormCol4}
          name="xrbData.requiredPackaging"
          label={t('Περιγραφή Συσκευασίας για Αποστολή')}
          disabled={true}
        />
      )}
      <FormRow>
        {data.xrbData.vermiculiteUsed && (
          <FieldInput
            as={FormCol6}
            label={t('Ποσότητα βερμικουλίτη')}
            name="xrbData.vermiculiteQuantity"
            disabled={true}
          />
        )}
      </FormRow>
      <FormRow>
        {data.xrbData.aeroplastUsed && (
          <FieldInput
            as={FormCol6}
            label={t('Ποσότητα αεροπλαστ')}
            name="xrbData.aeroplastQuantity"
            disabled={true}
          />
        )}
      </FormRow>
      <FormRow>
        {data.xrbData.plasticBagUsed && (
          <FieldInput
            as={FormCol6}
            label={t('Ποσότητα σακούλας')}
            name="xrbData.plasticBagQuantity"
            disabled={true}
          />
        )}
      </FormRow>
      <FormRow>
        {data.xrbData.polyethyleneUsed && (
          <FieldInput
            as={FormCol6}
            label={t('Ποσότητα πολυαιθυλενίου')}
            name="xrbData.polyethyleneQuantity"
            disabled={true}
          />
        )}
      </FormRow>
      <FormRow>
        {data.xrbData.otherMaterial && (
          <FieldInput
            as={FormCol6}
            label={t('Άλλο υλικό')}
            name="xrbData.otherMaterial"
            disabled={true}
          />
        )}
      </FormRow>
    </div>
  );
};
